import React from 'react';
import { fmtFull } from '../utils/formatters';
import { Demographics } from '../data';

interface DemographicsCardProps {
  demographics: Demographics;
}

export function DemographicsCard({ demographics }: DemographicsCardProps) {
  const maxAge = Math.max(...demographics.age.map(a => a.share));
  const genderColors: Record<string, string> = {
    Female: 'var(--brand-green-500)',
    Male:   'var(--brand-blue)',
    Unknown:'var(--fg-faint)',
  };

  // Total detections across the daypart buckets — the only "signal volume"
  // figure we actually have; age/gender shares are percentages (they always
  // sum to ~100) so they can't be used to derive a real signal count. The
  // daypart breakdown itself is shown as its own chart section, not here.
  const totalSignals = demographics.daypart.reduce((sum, d) => sum + d.v, 0);

  return (
    <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Audience</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-strong)' }}>Anonymous demographics</div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, color: 'var(--brand-green-500)',
          background: 'var(--accent-soft-bg)', border: '1px solid var(--accent-soft-border)',
          padding: '3px 8px', borderRadius: 999, display: 'inline-flex', gap: 4, alignItems: 'center',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-green-500)', display: 'inline-block' }} /> Live
        </span>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 11, color: 'var(--fg-muted)' }}>
          <span style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Gender split</span>
          <span className="num">{fmtFull(totalSignals)} signals</span>
        </div>
        <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-surface-3)' }}>
          {demographics.gender.map(g => (
            <div key={g.label} title={`${g.label} ${g.share}%`}
              style={{ width: `${g.share}%`, background: genderColors[g.label] || 'var(--fg-faint)' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          {demographics.gender.map(g => (
            <div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: genderColors[g.label] || 'var(--fg-faint)', display: 'inline-block' }} />
              <span style={{ color: 'var(--fg-muted)' }}>{g.label}</span>
              <span className="num" style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{g.share}%</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--fg-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Age bands</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {demographics.age.map(a => (
            <div key={a.band} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 44px', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 600 }}>{a.band}</span>
              <div style={{ height: 10, background: 'var(--bg-surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  width: `${(a.share / maxAge) * 100}%`, height: '100%',
                  background: a.share === maxAge ? 'var(--brand-green-500)' : 'linear-gradient(90deg, var(--brand-navy-800), var(--brand-navy-500))',
                  borderRadius: 999,
                }} />
              </div>
              <span className="num" style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-strong)', textAlign: 'right' }}>{a.share}%</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        padding: '8px 10px', background: 'var(--bg-surface-3)', borderRadius: 8,
        fontSize: 10.5, color: 'var(--fg-muted)', lineHeight: 1.45,
        display: 'flex', gap: 8, alignItems: 'flex-start',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3z" stroke="var(--brand-green-500)" strokeWidth="1.6" fill="var(--accent-soft-bg)" />
          <path d="m9 12 2 2 4-4" stroke="var(--brand-green-500)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span>Anonymous, aggregated. No face recognition, no personal identifiers.</span>
      </div>
    </div>
  );
}
